import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Typography,
  Flex,
  RemoteImage,
  IconChevronRight,
  IconHeartFilled,
  IconHeartOutline,
  Colors,
  ColorValues,
} from '@banhala/ui-kit';
import type { StoreListProps, StoreItem, StoreType } from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isNil, convertDeeplinkToWebLink, getWebBaseURL } from '../../../../utils';
import { useFavoriteMarket } from '../../../../hooks';
import { ABLY_INFO } from '../../../../constant/ablyInfo';

const DISPLAY_EVENT_MAP: Record<StoreType, string> = {
  MARKET: 'DISPLAY_MARKET',
  BRAND: 'DISPLAY_BRAND_HOME',
  BRAND_WITH_IMAGE: 'DISPLAY_BRAND_HOME',
  LICENSED_IP: 'DISPLAY_LICENSED_IP_HOME',
};

interface MarketListItemProps {
  item: StoreItem;
  logging: AnyObject;
}

const formatFavoritesCount = (count: number): string => {
  if (count === 0) return '0';
  if (count >= 10000) {
    const tenThousands = count / 10000;
    if (tenThousands % 1 === 0) {
      return `${tenThousands}만`;
    }
    return `${tenThousands.toFixed(1)}만`;
  }
  return count.toLocaleString();
};

const MarketListItem = ({ item, logging }: MarketListItemProps) => {
  const { name, profile, description, deeplink } = item;

  const { isFavorited, favoritesCount, toggleFavorite } = useFavoriteMarket({
    sno: item.sno,
    initialFavorited: item.favorite?.is_favorited ?? false,
    initialFavoritesCount: item.favorites_count ?? 0,
    logging,
  });

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_MARKET',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      bridge.openDeeplink(deeplink, {
        webCallback: async () => {
          const baseURL = await getWebBaseURL();
          const convertedLink = convertDeeplinkToWebLink(deeplink);
          if (convertedLink === ABLY_INFO.url) {
            location.href = ABLY_INFO.url;
            return;
          }
          location.href = `${baseURL}${convertedLink}`;
        },
      });
    }
  }, [deeplink, logging]);

  return (
    <Flex.Row
      alignItems="center"
      backgroundColor="white"
      padding={{ vertical: 16 }}
      gap={12}
    >
      {/* Tappable area - navigates to market */}
      <Flex.Row
        flex={1}
        alignItems="center"
        gap={12}
        padding={{ left: 16 }}
        onPress={handlePress}
      >
        {/* Profile Image */}
        {profile && (
          <Flex
            width={32}
            height={32}
            borderRadius={16}
            borderWidth={1}
            borderColor="border_transparent"
            overflow="hidden"
          >
            <RemoteImage uri={profile} width={32} height={32} />
          </Flex>
        )}

        {/* Text Content */}
        <Flex flex={1} gap={2}>
          <Typography
            type="subtitle2"
            color="content_primary"
            numberOfLines={1}
          >
            {name}
          </Typography>
          {description && (
            <Typography
              type="body3"
              color="content_tertiary"
              numberOfLines={1}
            >
              {description}
            </Typography>
          )}
        </Flex>
      </Flex.Row>

      {/* Favorite Button - separate pressable, alignSelf="stretch" for full row height */}
      <Flex
        alignSelf="stretch"
        alignItems="center"
        justifyContent="center"
        padding={{ right: 16 }}
        gap={2}
        onPress={toggleFavorite}
      >
        <Flex padding={{ horizontal: 10 }}>
          {isFavorited ? (
            <IconHeartFilled width={20} height={20} fill={Colors.content_pink_primary} />
          ) : (
            <IconHeartOutline width={20} height={20} fill={Colors.content_pink_primary} />
          )}
        </Flex>
        <Typography type="body4" color="content_pink_primary">
          {formatFavoritesCount(favoritesCount)}
        </Typography>
      </Flex>
    </Flex.Row>
  );
};

interface BrandListItemProps {
  item: StoreItem;
  logging: AnyObject;
  descriptionOverride?: string;
}

const BrandListItem = ({ item, logging, descriptionOverride }: BrandListItemProps) => {
  const { name, profile, description, deeplink } = item;

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_BRAND_HOME',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      bridge.openDeeplink(deeplink, {
        webCallback: async () => {
          const baseURL = await getWebBaseURL();
          const convertedLink = convertDeeplinkToWebLink(deeplink);
          if (convertedLink === ABLY_INFO.url) {
            location.href = ABLY_INFO.url;
            return;
          }
          location.href = `${baseURL}${convertedLink}`;
        },
      });
    }
  }, [deeplink, logging]);

  const displayDescription = descriptionOverride ?? description;

  return (
    <Flex.Row
      alignItems="center"
      padding={{ horizontal: 16, vertical: 16 }}
      gap={12}
      onPress={deeplink ? handlePress : undefined}
    >
      {/* Profile Image */}
      {profile && (
        <Flex
          width={32}
          height={32}
          borderRadius={16}
          borderWidth={1}
          borderColor="border_transparent"
          overflow="hidden"
        >
          <RemoteImage uri={profile} width={32} height={32} />
        </Flex>
      )}

      {/* Text Content */}
      <Flex flex={1} gap={4}>
        <Typography
          type="subtitle2"
          color="content_primary"
          numberOfLines={1}
        >
          {name}
        </Typography>
        {displayDescription && (
          <Typography
            type="body3"
            color="content_secondary"
            numberOfLines={1}
          >
            {displayDescription}
          </Typography>
        )}
      </Flex>

      {/* Chevron Icon */}
      <IconChevronRight width={16} height={16} color="content_tertiary" />
    </Flex.Row>
  );
};

interface BrandWithImageItemProps {
  item: StoreItem;
  logging: AnyObject;
}

const BrandWithImageItem = ({ item, logging }: BrandWithImageItemProps) => {
  const { name, description, deeplink, goods_images } = item;

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_BRAND_HOME',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      bridge.openDeeplink(deeplink, {
        webCallback: async () => {
          const baseURL = await getWebBaseURL();
          const convertedLink = convertDeeplinkToWebLink(deeplink);
          if (convertedLink === ABLY_INFO.url) {
            location.href = ABLY_INFO.url;
            return;
          }
          location.href = `${baseURL}${convertedLink}`;
        },
      });
    }
  }, [deeplink, logging]);

  const images = goods_images?.slice(0, 3) ?? [];
  const imageCount = images.length;

  return (
    <Flex padding={{ horizontal: 16, vertical: 5 }}>
      <Flex.Row
        alignItems="center"
        height={80}
        backgroundColor="gray10"
        borderRadius={8}
        padding={{ horizontal: 12 }}
        onPress={deeplink ? handlePress : undefined}
      >
        <View style={styles.brandWithImageText}>
          <Flex gap={4}>
            <Typography
              type="subtitle2"
              color="content_primary"
              numberOfLines={1}
            >
              {name}
            </Typography>
            {description && (
              <Typography
                type="body4"
                color="content_secondary"
                numberOfLines={1}
              >
                {description}
              </Typography>
            )}
          </Flex>
        </View>

        {/* Overlapping Product Images (9dp overlap) */}
        {images.length > 0 && (
          <View style={styles.overlappingImages}>
            {images.map((imageUrl, index) => (
              <View
                key={index}
                style={[
                  styles.goodsImageContainer,
                  index > 0 && { marginLeft: -9 },
                  { zIndex: imageCount - index },
                ]}
              >
                <RemoteImage uri={imageUrl} width={56} height={56} />
              </View>
            ))}
          </View>
        )}
      </Flex.Row>
    </Flex>
  );
};

const Divider = () => <View style={styles.divider} />;

export const StoreList = ({ entity }: StoreListProps) => {
  const displayLoggedRef = useRef(false);

  useEffect(() => {
    if (displayLoggedRef.current || isNil(entity) || entity.length === 0) {
      return;
    }
    displayLoggedRef.current = true;
    entity.forEach((data) => {
      const eventName = DISPLAY_EVENT_MAP[data.item.type];
      if (eventName) {
        bridge.logAnalyticsEvent({
          name: eventName,
          analytics: data.logging.analytics,
          ably_only_log: data.logging.ably_only_log,
        });
      }
    });
  }, [entity]);

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  const totalSize = entity.length;

  return (
    <Flex>
      {entity.map((data, index) => {
        const { type, sno } = data.item;
        const isLast = index === totalSize - 1;
        const showDivider = type !== 'BRAND_WITH_IMAGE' && !isLast;

        let component: React.ReactNode;
        switch (type) {
          case 'MARKET':
            component = <MarketListItem item={data.item} logging={data.logging} />;
            break;
          case 'BRAND':
            component = (
              <BrandListItem
                item={data.item}
                logging={data.logging}
                descriptionOverride="브랜드 모아보기"
              />
            );
            break;
          case 'LICENSED_IP':
            component = <BrandListItem item={data.item} logging={data.logging} />;
            break;
          case 'BRAND_WITH_IMAGE':
            component = <BrandWithImageItem item={data.item} logging={data.logging} />;
            break;
          default:
            component = <BrandListItem item={data.item} logging={data.logging} />;
        }

        return (
          <Flex key={sno}>
            {component}
            {showDivider && <Divider />}
          </Flex>
        );
      })}
    </Flex>
  );
};

const styles = StyleSheet.create({
  brandWithImageText: {
    flex: 1,
    marginRight: 6,
  },
  overlappingImages: {
    flexDirection: 'row',
  },
  goodsImageContainer: {
    width: 60,
    height: 60,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: ColorValues.gray10,
    backgroundColor: ColorValues.gray10,
    overflow: 'hidden',
  },
  divider: {
    height: 1,
    backgroundColor: ColorValues.gray20,
  },
});
